import {
  cspWithoutActivity,
  findRequiredConnectionKeys,
  findRequiredConnectionKeysLv2,
  RequiredConnectionKeys,
  RequiredConnectionKeysLv2
} from "@/features/flow/constants";
import type {
  DeclarativeBirth,
  DeclarativeMonthlyIncome,
  DeclarativeNif,
  DeclarativeOccupation,
  DeclarativePerson,
  DeclarativeResidence
} from "@/features/flow/types";
import { FullCountry, NaturalPerson, NaturalPersonDTO } from "@/features/user/types";
import { CSPCodesNoActivity } from "@/features/utils/constants";
import { SignupState } from "@/models/signup";
import { DateTime } from "luxon";
import { mapSignupStateToNp } from "./mappers.utils";

export function isDeclarativeNationalityValid(data: DeclarativePerson | undefined | null) {
  if (!data) return false;

  return !!data.nationalityCodes?.length && !!data.nationalityCodes[0];
}

export function isDeclarativeResidenceValid(data: DeclarativeResidence | null | undefined) {
  if (!data) return false;
  return (
    !!data.addressLine?.trim() && !!data.city?.trim() && !!data.zip?.trim() && !!data.countryCode
  );
}

export function isCSPCodeNoOccupation(CSPcode: number) {
  return CSPCodesNoActivity.find((csp) => csp.Code === CSPcode);
}

// Activity can be null only if CSP code allows for it. CSP code must be enterred
export function isDeclarativeOccupationValid(
  data: DeclarativeOccupation | undefined | null
): boolean {
  if (!data) return false;

  const noOccupation = isCSPCodeNoOccupation(data.CSPcode!);
  return (
    (noOccupation !== undefined || (data.activity !== null && data.activity !== undefined)) &&
    data.CSPcode !== null &&
    data.CSPcode !== undefined
  );
}

// MonthlyIncome can be null only if CSP code allows for it. CSP code must be enterred
export function isDeclarativeMonthlyIncomeValid(data: DeclarativeMonthlyIncome | undefined | null) {
  if (!data) return false;
  if (!data.trans_key) return false;
  return true;
}

export const monthlyIncomes: DeclarativeMonthlyIncome[] = [
  {
    trans_key: "less_1500",
    minimum: 0,
    maximum: 1500
  },
  {
    trans_key: "between_1500_and_5000",
    minimum: 1500,
    maximum: 5000
  },
  {
    trans_key: "between_5000_and_10000",
    minimum: 5000,
    maximum: 10000
  },
  {
    trans_key: "more_10000",
    minimum: 10000,
    maximum: 0
  }
];

export function isDeclarativeNifValid(data: DeclarativeNif[] | undefined | null) {
  if ((data && data.length === 0) || data === null) return false;
  return data!.length > 0;
}

export function isDeclarativeBirthValid(data: DeclarativeBirth | null | undefined) {
  if (!data) return false;
  return (
    !!data.birth && !!data.cityOfBirth?.trim() && !!data.countryCode && data.countryCode !== "NaN"
  );
}

export function isBirthDateValid(date?: Date | null) {
  if (!date) {
    return false;
  }
  const min = DateTime.now().minus({ years: 18 });
  if (min < DateTime.fromJSDate(date)) {
    return false;
  }
  return true;
}

export const isIdentityValid = (data: NaturalPersonDTO) => {
  if (!Object.values(cspWithoutActivity).includes(data.CSPCode))
    return (
      data.LastName?.trim() &&
      data.FirstName?.trim() &&
      data.CountryOfCitizenship &&
      data.countryOfBirth &&
      data.DateOfBirth?.date &&
      data.CSPCode &&
      data.ActivityCode &&
      data.StateOfBirth?.trim()
    );
  else
    return (
      data.LastName?.trim() &&
      data.FirstName?.trim() &&
      data.CountryOfCitizenship &&
      data.countryOfBirth &&
      data.DateOfBirth?.date &&
      data.CSPCode &&
      data.StateOfBirth?.trim()
    );
};

export const getRequiredConnectionKeysFlow = (
  countries: FullCountry[],
  signupState: SignupState,
  profile: NaturalPerson,
  olkypassLevel: number
): RequiredConnectionKeys | RequiredConnectionKeysLv2 => {
  const countryOfCitizenship = countries?.find(
    (country) => country.id.toString() === profile.CountryOfCitizenship?.toString()
  )?.id;

  const countryOfCitizenshipSignupState = countries?.find(
    (country) => country.alpha2 === signupState.identity?.person?.nationalityCodes[0]
  )?.id;

  const npFromSignupState = mapSignupStateToNp(
    signupState,
    countryOfCitizenshipSignupState ?? countryOfCitizenship
  );
  return olkypassLevel === 1
    ? findRequiredConnectionKeys(npFromSignupState)
    : findRequiredConnectionKeysLv2(npFromSignupState);
};
